import { useMemo } from 'react';

import { FeatureFlagSet } from '~/contexts/FeatureFlagProvider/featureFlags';

type FacilityUiVisibility = {
  fundingRequest: {
    showVerificationReportIfAvailable: boolean; // show verification report if available in the individual funding request page
  };
  verification: {
    showAssetVerification: boolean; // show results from asset verification
    showAssetVerificationReportUpload: boolean; // show 'Upload New Report' button
    showAdjudicationActions: boolean; // show ability to take verification adjudication actions
    showNotifyReportReadyAction: boolean; // show ability to send notification that verification report is ready for review
    showRequestRemediationAction: boolean; // show request remediation button
    showWaiveReceivableEligibilityActions: boolean; // show ability to take receivable eligibility waiver actions
    showConcentrationLimitsInsightsLink: boolean; // show link to concentration limit insights on the verification report
  };
};

const mockBorrowerOwnerFacilityUiVisibility: FacilityUiVisibility = {
  fundingRequest: {
    showVerificationReportIfAvailable: false,
  },
  verification: {
    showAssetVerification: false,
    showAssetVerificationReportUpload: false,
    showAdjudicationActions: false,
    showNotifyReportReadyAction: false,
    showRequestRemediationAction: false,
    showWaiveReceivableEligibilityActions: false,
    showConcentrationLimitsInsightsLink: false,
  },
};

const mockBorrowerLimitedFacilityUiVisibility: FacilityUiVisibility = {
  fundingRequest: {
    showVerificationReportIfAvailable: true,
  },
  verification: {
    showAssetVerification: true,
    showAssetVerificationReportUpload: false, // Currently hiding for borrowers.
    showAdjudicationActions: false,
    showNotifyReportReadyAction: true,
    showRequestRemediationAction: false,
    showWaiveReceivableEligibilityActions: true,
    showConcentrationLimitsInsightsLink: false,
  },
};

const mockLenderOwnerWithBorrowerLimitedFacilityUiVisibility: FacilityUiVisibility = {
  fundingRequest: {
    showVerificationReportIfAvailable: true,
  },
  verification: {
    showAssetVerification: true,
    // Won't work yet for all facilities, but currently the only one that will show a non-working
    // upload button is presidio, a demo account.
    showAssetVerificationReportUpload: true,
    showAdjudicationActions: true,
    showNotifyReportReadyAction: false,
    showRequestRemediationAction: true,
    showWaiveReceivableEligibilityActions: true,
    showConcentrationLimitsInsightsLink: true,
  },
};

const mockLenderOwnerWithoutBorrowerFacilityUiVisibility: FacilityUiVisibility = {
  fundingRequest: {
    showVerificationReportIfAvailable: true,
  },
  verification: {
    showAssetVerification: true,
    showAssetVerificationReportUpload: false, // code does not correctly handle all facilities yet.
    showAdjudicationActions: true,
    showNotifyReportReadyAction: false,
    showRequestRemediationAction: false,
    showWaiveReceivableEligibilityActions: true,
    showConcentrationLimitsInsightsLink: true,
  },
};

type MockUiVisibilityMode =
  | 'lenderOwnerWithBorrowerLimited'
  | 'lenderOwnerWithoutBorrower'
  | 'borrowerOwner'
  | 'borrowerLimited';

const mockFacilityUiVisibilityToModeMap: {
  [key in MockUiVisibilityMode]: FacilityUiVisibility;
} = {
  lenderOwnerWithBorrowerLimited: mockLenderOwnerWithBorrowerLimitedFacilityUiVisibility,
  lenderOwnerWithoutBorrower: mockLenderOwnerWithoutBorrowerFacilityUiVisibility,
  borrowerOwner: mockBorrowerOwnerFacilityUiVisibility,
  borrowerLimited: mockBorrowerLimitedFacilityUiVisibility,
};

export const getMockUiVisibilityMode = ({
  flags,
  defaultTo,
}: {
  flags: FeatureFlagSet | undefined;
  defaultTo: MockUiVisibilityMode;
}): MockUiVisibilityMode => {
  if (!flags) {
    return defaultTo;
  }
  if (flags.lenderOwnerOrganizationWithBorrowerLimited) {
    return 'lenderOwnerWithBorrowerLimited';
  }
  if (flags.isBorrowerLimitedOrganization) {
    return 'borrowerLimited';
  }
  return defaultTo;
};

// @JonAtFinley @danitian replace with real UiVisibility returned from auth
export const useCreditFacilityUiVisibility = ({
  mockUiVisibilityMode,
}: {
  mockUiVisibilityMode: MockUiVisibilityMode;
  creditFacilityId: string;
}) => {
  return useMemo(
    () => mockFacilityUiVisibilityToModeMap[mockUiVisibilityMode],
    [mockUiVisibilityMode],
  );
};
